import { useQuery } from '@tanstack/react-query';
import { useEnabledQuery } from '../../../ui/hooks/useEnabledQuery';
import { ResourceType } from '../../enumerations/ResourceType';
import { get } from '../APIVersion1';
import { createGetAllOrSingleObjectSchema, ParentedArtifactResource } from '../artifactResourceTypes';
import { PersistableSchemaType } from '../persistableResourceTypes';
import { ParamConstants } from '../types';
import { parseRawData, useOnUnauthorized } from './AdapterUtils';
import { useResourceAdapterActions } from './useResourceAdapterActions';

export function useMultiAndSingleResourceMultiParentedResourceAdapterBaseActions<R extends ParentedArtifactResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>,
) {
    const onUnauthorized = useOnUnauthorized();

    const actions = useResourceAdapterActions(apiPostfix, resourceType, resourceSchema);
    const getEnabled = useEnabledQuery();

    const evaluateGetAllOrSingleResult = (result: Response): Promise<Array<R> | R | null> => {
        if (result.status !== 200) {
            return Promise.reject('Resources could not be found!');
        }
        return result
            .json()
            .then((rawData) => parseRawData(rawData, createGetAllOrSingleObjectSchema(resourceSchema)))
            .then((jsonResult) => {
                if (jsonResult.all) {
                    return jsonResult.all;
                } else if (jsonResult.single) {
                    return jsonResult.single;
                }
                return null;
            });
    };

    const getAllOrSingleWithParent = (parentId: string, parentRelation: string): Promise<Array<R> | R | null> => {
        return get(
            `${apiPostfix}?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}${actions.getParentRelationText(parentRelation)}&${
                ParamConstants.PER_PAGE_REQUEST_PARAM_NAME
            }=400`,
        ).then((result) => {
            if (result.status === 401) {
                return onUnauthorized(result, null);
            } else {
                return evaluateGetAllOrSingleResult(result);
            }
        });
    };

    // TODO use or remove
    const useGetAllOrSingleResources = (parentRelation: string, parentId: string | undefined) => {
        return useQuery({
            queryKey: [parentId, parentRelation, resourceType],
            queryFn: () => getAllOrSingleWithParent(parentId as string, parentRelation),
            enabled: getEnabled(parentId),
        });
    };

    const useGetAllResources = (parentRelation: string, parentId: string | undefined) => {
        return useQuery({
            queryKey: [parentId, parentRelation, resourceType],
            queryFn: () => getAllOrSingleWithParent(parentId as string, parentRelation) as Promise<R[] | null>,
            enabled: getEnabled(parentId),
        });
    };

    const useGetSingleResource = (parentRelation: string, parentId: string | undefined) => {
        return useQuery({
            queryKey: [parentId, parentRelation, resourceType],
            queryFn: () => getAllOrSingleWithParent(parentId as string, parentRelation) as Promise<R | null>,
            enabled: getEnabled(parentId),
        });
    };

    return {
        addOrSetResource: actions.addOrSetResource,
        addResourceToIndex: actions.addResourceToIndex,
        getSingleFromParent: actions.getSingleFromParent,
        getAllResourcesWithParent: actions.getAllResourcesWithParent,
        changeIndexOfResource: actions.changeIndexOfResource,
        getAllResources: actions.getAllResources,

        useValidateResource: actions.useValidateResource,
        useValidateAllResourcesMultiParented: actions.useValidateAllResourcesMultiParented,
        useValidateAllResourcesSingleParented: actions.useValidateAllResourcesSingleParented,
        useGetResource: actions.useGetResource,
        useUpdateResource: actions.useUpdateResource,
        useDeleteResource: actions.useDeleteResource,
        processMutationResponse: actions.processMutationResponse,
        processGetResponse: actions.processGetResponse,
        processGetCollectionResponse: actions.processGetCollectionResponse,
        processGetResourceResponse: actions.processGetResourceResponse,
        useGetAllOrSingleResources,
        useGetAllResources,
        useGetSingleResource,
    };
}
